import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MessageSquare } from "lucide-react";

export default function SmsCenter() {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [promo, setPromo] = useState("");
  const { data: logs = [] } = useQuery({
    queryKey: ["sms"],
    queryFn: async () => (await api.get("/sms")).data,
    refetchInterval: 8000,
  });

  const send = async () => {
    try {
      const { data } = await api.post("/sms", { to, body });
      toast.success(data.status === "sent" ? "SMS sent" : `Logged (${data.status}). Add TWILIO_SMS_FROM to send live.`);
      setBody("");
      qc.invalidateQueries({ queryKey: ["sms"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const blast = async () => {
    try {
      const { data } = await api.post("/sms/broadcast", { body: promo });
      toast.success(`Queued to ${data.sent} customers`);
      setPromo("");
      qc.invalidateQueries({ queryKey: ["sms"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="sms-center">
      <PageHeader title="SMS Service" subtitle="Order alerts, ready-to-collect texts, and CRM broadcasts" />
      <div className="grid lg:grid-cols-2 gap-4 max-w-5xl">
        <div className="kul-card p-5 space-y-3">
          <h3 className="font-display font-700 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Send SMS</h3>
          <div><Label>Phone</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+91…" /></div>
          <div><Label>Message</Label><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="w-full rounded-lg border p-2 text-sm" /></div>
          <Button onClick={send} className="bg-[var(--kul-primary)] text-white">Send</Button>
          <div className="border-t pt-3">
            <Label>Broadcast to all CRM phones</Label>
            <textarea value={promo} onChange={(e) => setPromo(e.target.value)} rows={2} className="w-full rounded-lg border p-2 text-sm mt-1" placeholder="Weekend 20% off…" />
            <Button variant="outline" className="mt-2" onClick={blast}>Broadcast</Button>
          </div>
        </div>
        <div className="kul-card p-5">
          <h3 className="font-display font-700 mb-3">Outbox</h3>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {logs.map((l) => (
              <div key={l.id} className="text-sm border-b pb-2">
                <div className="flex justify-between">
                  <span className="font-medium">{l.to || "—"}</span>
                  <Badge className="capitalize">{l.status}</Badge>
                </div>
                <div className="text-neutral-600 mt-1">{l.body}</div>
                <div className="text-[11px] text-neutral-400">{l.kind} · {fmtTime(l.created_at)}</div>
              </div>
            ))}
            {!logs.length && <p className="text-neutral-400">No messages yet. Kitchen “food ready” auto-texts the guest phone.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
